import { defineStore } from 'pinia';
import MutationCall from '~/graphql/wrappers/mutation-call';
import QueryCall from '~/graphql/wrappers/query-call';
import type { CourseListOptions, TimetableEntries } from '~/utils/utilTypes';
import  {
  FeatureFlagsDocument,
  type FeatureFlagsQuery,
  type FeatureFlagsQueryVariables,
  PushOnboardingProgressDocument,
  type PushOnboardingProgressMutation,
  type PushOnboardingProgressMutationVariables,
} from '~/graphql';

//let pushTimetableMutation: MutationCall<PushTimetableMutation, PushTimetableMutationVariables>;
let pushOnboardingProgressMutation: MutationCall<
  PushOnboardingProgressMutation,
  PushOnboardingProgressMutationVariables
>;
let featureQuery: QueryCall<FeatureFlagsQuery, FeatureFlagsQueryVariables>;

export const useFeaturesManagement = defineStore('featuresManagement', {
  state: () => ({
    features: {},
    timetable: [] as TimetableEntries,
    isComplexOnly: false,
    onboardingProgress: {},
    onboardingProgressLoaded: false,
  }),
  getters: {
    //returns all features
    getFeatures(state: any): any {
      return state.features;
    },
    //returns extracted timetable
    getTimetable(state: any): TimetableEntries {
      return state.timetable;
    },
  },
  actions: {
    async init() {
      //pushTimetableMutation = new MutationCall(PushTimetableDocument);
      pushOnboardingProgressMutation = new MutationCall(
        PushOnboardingProgressDocument,
      );
      featureQuery = new QueryCall<FeatureFlagsQuery>(FeatureFlagsDocument);
    },
    //fetches features
    async fetchFeatures() {
      const features = await featureQuery.run();
      if (features.hasError()) {
        throw features.getError();
      }
      this.features = features.getData(); //raw data
      return this.features;
    },
    async fetchOnboardingProgress() {
      if (this.onboardingProgressLoaded) {
        return this.onboardingProgress;
      }
      const features = await featureQuery.run();
      if (features.hasError()) {
        throw features.getError();
      }
      this.onboardingProgress = getMyFeatureSettings(
        features.getData(),
        'onboarding_progress',
      )?.progress;
      this.onboardingProgressLoaded = true;
      if (!this.onboardingProgress) {
        this.onboardingProgress = {};
      }
      return this.onboardingProgress;
    },

    isTimetableValid(myCourses: CourseListOptions) {
      return validateTimetableFormEntries(this.timetable, myCourses);
    },

    async pushOnboardingProgressProperty(property, value) {
      if (!this.onboardingProgressLoaded) {
        await this.fetchOnboardingProgress();
      }
      if (
        this.onboardingProgress[property] &&
        this.onboardingProgress[property] === value
      ) {
        return;
      }
      this.onboardingProgress[property] = value;
      await this.pushOnboardingProgress(this.onboardingProgress);
    },

    async pushOnboardingProgress(
      onboardingProgress: PushOnboardingProgressMutationVariables,
    ) {
      this.onboardingProgress = onboardingProgress;
      const result = await pushOnboardingProgressMutation.run({
        pushOnboardingProgressInput: {
          progress: onboardingProgress,
        },
      });
      if (result.hasError()) {
        throw result.getError();
      }
      return;
    },
  },
});

//-----------------------------------------------------------------
//feature helpers
//-----------------------------------------------------------------

//checks for a feature key and returns the feature (containing key, settings)
function getMyFeature(myFeaturesData: any, myFeaturesKey: string) {
  if (
    myFeaturesData?.config?.featureFlags &&
    Array.isArray(myFeaturesData?.config?.featureFlags) &&
    myFeaturesData?.config?.featureFlags.length > 0
  ) {
    for (let i = 0; i < myFeaturesData.config.featureFlags.length; i++) {
      if (myFeaturesData.config.featureFlags[i]?.key === myFeaturesKey) {
        return myFeaturesData.config.featureFlags[i];
      }
    }
  }
  return undefined;
}

//checks for specific feature settings living in parallel to the feature key
function getMyFeatureSettings(myFeaturesData: any, myFeatureKey: string) {
  const myFeature = getMyFeature(myFeaturesData, myFeatureKey);
  if (myFeature?.settings) {
    return myFeature?.settings;
  }
  return undefined;
}

//-----------------------------------------------------------------
//timetable helpers
//-----------------------------------------------------------------

//basic validation of the timetable entries
function validateTimetableFormEntries(
  myTimetable: TimetableEntries,
  myCourses: CourseListOptions,
) {
  let timetableValid = true;
  for (const row of myTimetable) {
    if (
      !myCourses.find((course) => {
        return course.id === row.course;
      })
    ) {
      timetableValid = false;
      break;
    }
    if (!row.day_of_week) {
      timetableValid = false;
      break;
    }
    if (!(Number(row.day_of_week) >= 0 && Number(row.day_of_week) <= 6)) {
      timetableValid = false;
      break;
    }
    if (row.from == undefined || row.from === '') {
      timetableValid = false;
      break;
    }
    if (row.from == undefined || row.from === '') {
      timetableValid = false;
      break;
    }
    if (row.from > row.to) {
      timetableValid = false;
      break;
    }
  }
  return timetableValid;
}
